<template>
  <div class="netsis-status">
    <!-- DB ikonu + duruma göre renkli nokta -->
    <b-button
      :id="targetId"
      variant="flat-secondary"
      class="btn-icon rounded-circle netsis-status-btn position-relative"
    >
      <feather-icon icon="DatabaseIcon" size="20" />
      <span class="netsis-status-dot" :class="dotClass" />
    </b-button>

    <!-- Tıklanınca açılan detay popover -->
    <b-popover
      :target="targetId"
      triggers="click blur"
      placement="right"
      custom-class="netsis-status-popover"
    >
      <template #title>
        <div class="d-flex align-items-center justify-content-between">
          <span>Netsis Durumu</span>
          <b-badge :variant="badgeVariant" pill>
            {{ statusText }}
          </b-badge>
        </div>
      </template>

      <div class="netsis-status-body">
        <div class="netsis-row">
          <span class="netsis-label">Son ping</span>
          <span class="netsis-value">{{ lastPingText }}</span>
        </div>
        <div class="netsis-row">
          <span class="netsis-label">Sonraki ping</span>
          <span class="netsis-value">{{ nextPingText }}</span>
        </div>
        <div class="netsis-row">
          <span class="netsis-label">Gecikme</span>
          <span class="netsis-value">{{ latencyText }}</span>
        </div>
        <div
          v-if="snapshot && !snapshot.Healthy && snapshot.Message"
          class="netsis-message text-danger"
        >
          {{ snapshot.Message }}
        </div>
      </div>
    </b-popover>
  </div>
</template>

<script>
import { BButton, BPopover, BBadge } from 'bootstrap-vue'
import { NetsisStatusR } from '@/utilities/system/signalr/netsis-status'

// Bu gösterge HİÇBİR istek atmaz. Backend arka planda Netsis'i pingler
// (örn. 15 sn'de bir) ve sonucu SignalR ile "updateNetsisStatus" event'iyle
// yayınlar; biz yalnızca dinleyip gösteririz.
export default {
  name: 'NetsisStatusIndicator',
  components: {
    BButton,
    BPopover,
    BBadge,
  },
  data() {
    return {
      targetId: 'netsis-status-indicator',
      hub: null,
      snapshot: null,
      connectionState: 'connecting', // connecting | connected | reconnecting | disconnected
      secondsUntilNext: 0,
      lastReceivedAt: null,
      nowTick: Date.now(),
      timer: null,
    }
  },
  computed: {
    isUp() {
      return this.connectionState === 'connected' && this.snapshot && this.snapshot.Healthy
    },
    isDown() {
      return this.connectionState === 'connected' && this.snapshot && !this.snapshot.Healthy
    },
    dotClass() {
      if (this.isUp) return 'is-up'
      if (this.isDown) return 'is-down'
      return 'is-unknown'
    },
    statusText() {
      if (this.connectionState === 'reconnecting') return 'Yeniden bağlanıyor'
      if (this.connectionState !== 'connected') return 'Bağlanıyor'
      if (!this.snapshot) return 'Bekleniyor'
      return this.snapshot.Status || (this.snapshot.Healthy ? 'Bağlı' : 'Bağlantı Yok')
    },
    badgeVariant() {
      if (this.isUp) return 'success'
      if (this.isDown) return 'danger'
      return 'secondary'
    },
    lastPingText() {
      if (!this.snapshot || !this.snapshot.CheckedAt) return '—'
      const checked = new Date(this.snapshot.CheckedAt)
      const clock = checked.toLocaleTimeString('tr-TR')
      const ago = this.lastReceivedAt
        ? Math.max(0, Math.round((this.nowTick - this.lastReceivedAt) / 1000))
        : null
      return ago !== null ? `${clock} (${ago} sn önce)` : clock
    },
    nextPingText() {
      if (this.connectionState !== 'connected') return '—'
      return `${this.secondsUntilNext} sn`
    },
    latencyText() {
      if (!this.snapshot || this.snapshot.LatencyMs == null) return '—'
      return `${this.snapshot.LatencyMs} ms`
    },
  },
  mounted() {
    // Sadece dinle: backend SignalR ile snapshot yayınlar.
    this.hub = new NetsisStatusR({
      onUpdate: this.handleUpdate,
      onStateChange: this.handleStateChange,
    })
    this.timer = setInterval(this.tick, 1000)
  },
  beforeDestroy() {
    if (this.timer) clearInterval(this.timer)
    if (this.hub) this.hub.Stop()
  },
  methods: {
    handleUpdate(raw) {
      if (!raw) {
        this.snapshot = null
        return
      }
      // SignalR varsayılan olarak camelCase serialize eder; hem camelCase
      // hem PascalCase'i destekleyelim ki backend ayarı değişse de çalışsın.
      const get = (camel, pascal) => (raw[camel] !== undefined ? raw[camel] : raw[pascal])
      this.snapshot = {
        Healthy: get('healthy', 'Healthy'),
        Status: get('status', 'Status'),
        Message: get('message', 'Message'),
        LatencyMs: get('latencyMs', 'LatencyMs'),
        CheckedAt: get('checkedAt', 'CheckedAt'),
        IntervalSeconds: get('intervalSeconds', 'IntervalSeconds'),
      }
      this.lastReceivedAt = Date.now()
      this.secondsUntilNext = this.snapshot.IntervalSeconds || 15
    },
    handleStateChange(state) {
      this.connectionState = state
    },
    tick() {
      this.nowTick = Date.now()
      if (this.secondsUntilNext > 0) this.secondsUntilNext -= 1
    },
  },
}
</script>

<style lang="scss" scoped>
.netsis-status {
  display: flex;
  align-items: center;
  justify-content: center;
}

.netsis-status-btn {
  padding: 0.5rem;
  // Koyu lacivert menü rayında görünür olması için ikon açık renk
  color: rgba(255, 255, 255, 0.6) !important;

  &:hover,
  &:focus {
    color: #fff !important;
    background-color: rgba(255, 255, 255, 0.08) !important;
  }
}

.netsis-status-dot {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  border: 2px solid #fff;
  box-sizing: content-box;

  &.is-up {
    background-color: #28c76f;
    box-shadow: 0 0 0 rgba(40, 199, 111, 0.6);
    animation: netsis-pulse 1.8s infinite;
  }
  &.is-down {
    background-color: #ea5455;
  }
  &.is-unknown {
    background-color: #b9b9c3;
  }
}

@keyframes netsis-pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(40, 199, 111, 0.5);
  }
  70% {
    box-shadow: 0 0 0 6px rgba(40, 199, 111, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(40, 199, 111, 0);
  }
}
</style>

<style lang="scss">
.netsis-status-popover {
  min-width: 240px;

  .netsis-status-body {
    .netsis-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0.25rem 0;

      & + .netsis-row {
        border-top: 1px solid rgba(34, 41, 47, 0.08);
      }
    }
    .netsis-label {
      color: #6e6b7b;
    }
    .netsis-value {
      font-weight: 600;
    }
    .netsis-message {
      margin-top: 0.5rem;
      font-size: 0.85rem;
      word-break: break-word;
    }
  }
}

// Dark mode uyumu
.dark-layout .netsis-status-dot {
  border-color: #283046;
}
.dark-layout .netsis-status-popover .netsis-status-body .netsis-row + .netsis-row {
  border-top-color: rgba(255, 255, 255, 0.08);
}
</style>
